/* Both review regions share one download and keep independent pagination. */
(() => {
  if (customElements.get('motoptes-customer-reviews')) return;
  const downloads = new Map();
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class CustomerReviews extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.page = 1;
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-page]');
        if (!button || !this.contains(button)) return;
        this.page = Number(button.dataset.page);
        this.render();
        this.querySelector('[data-results]').focus({ preventScroll: true });
        this.querySelector('[data-results]').scrollIntoView({ block: 'nearest' });
      }, { signal: this.controller.signal });
      this.load(this.controller.signal);
    }
    disconnectedCallback() { this.controller?.abort(); }
    async load(signal) {
      const url = this.dataset.source;
      try {
        if (!downloads.has(url)) downloads.set(url, fetch(url).then(response => {
          if (!response.ok) throw new Error('Reviews unavailable');
          return response.json();
        }).catch(error => { downloads.delete(url); throw error; }));
        const data = await downloads.get(url);
        if (signal.aborted) return;
        this.reviews = data.reviews.filter(review => this.dataset.mode === 'store' || review.products.includes(this.dataset.product));
        this.render();
        // Keep empty product sections and loading/error headings out of the page.
        const region = this.closest('[data-review-region]');
        if (region && this.dataset.mode === 'product') region.hidden = this.reviews.length === 0;
      } catch {
        if (!signal.aborted) this.querySelector('[data-status]').textContent = this.dataset.error;
      }
    }
    card(review) {
      const card = element('article', 'mcr__card');
      const header = element('header', 'mcr__byline');
      header.append(element('strong', '', review.name));
      if (review.country) header.append(element('span', 'mcr__muted', review.country));
      const stars = element('span', 'mcr__stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating} / 5`);
      card.append(header, stars);
      const date = element('time', 'mcr__date', review.date);
      date.dateTime = review.date;
      card.append(date);
      if (review.body) card.append(element('p', 'mcr__body', review.body));
      if (review.images.length) {
        const photos = element('div', 'mcr__photos');
        for (const [index, url] of review.images.entries()) {
          if (!/^https:\/\//.test(url)) continue;
          const link = element('a', 'mcr__photo');
          link.href = url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          const img = element('img');
          img.src = url;
          img.alt = `${this.dataset.photo} ${index + 1}`;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.width = 240;
          img.height = 240;
          link.append(img);
          photos.append(link);
        }
        card.append(photos);
      }
      if (this.dataset.mode === 'store') {
        const content = element('div', 'mcr__content');
        const photos = card.querySelector('.mcr__photos');
        for (const child of Array.from(card.children)) {
          if (child !== photos) content.append(child);
        }
        card.replaceChildren(content);
        if (photos) card.append(photos);
      }
      return card;
    }
    render() {
      const total = this.reviews.length;
      const pageSize = this.dataset.mode === 'store' ? 6 : 5;
      const pages = Math.max(1, Math.ceil(total / pageSize));
      this.page = Math.max(1, Math.min(this.page, pages));
      const results = this.querySelector('[data-results]');
      results.replaceChildren(...this.reviews.slice((this.page - 1) * pageSize, this.page * pageSize).map(review => this.card(review)));
      this.querySelector('[data-status]').textContent = total ? `${(this.page - 1) * pageSize + 1}–${Math.min(this.page * pageSize, total)} / ${total}` : this.dataset.empty;
      const nav = this.querySelector('[data-pagination]');
      nav.replaceChildren();
      nav.hidden = pages <= 1;
      const addButton = (label, page, disabled = false) => {
        const button = element('button', 'mcr__page', label);
        button.type = 'button';
        button.dataset.page = page;
        button.disabled = disabled;
        if (String(label) === String(this.page)) button.setAttribute('aria-current', 'page');
        nav.append(button);
      };
      addButton(this.dataset.previous, this.page - 1, this.page === 1);
      const visible = new Set([1, pages]);
      for (let page = Math.max(1, this.page - 2); page <= Math.min(pages, this.page + 2); page++) visible.add(page);
      let last = 0;
      for (const page of [...visible].sort((a, b) => a - b)) {
        if (page > last + 1) nav.append(element('span', 'mcr__ellipsis', '…'));
        addButton(String(page), page);
        last = page;
      }
      addButton(this.dataset.next, this.page + 1, this.page === pages);
    }
  }
  customElements.define('motoptes-customer-reviews', CustomerReviews);
})();
